/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import css from '@emotion/css/macro'
import { LineChartOutlined } from '@ant-design/icons'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { usePlayerStatsTab } from '@sportninja/common/features/profile/stats/hooks/usePlayerStatsTab'
import { getRoleTitle } from '@sportninja/common/features/profile/stats/utils/roleTitles'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'
import { useQueryClient } from '@tanstack/react-query'

import EmptyState from 'src/components/ts-components/EmptyState'
import LoadErrorState from 'src/components/ts-components/LoadErrorState'
import SelectTab from 'src/components/ts-components/SelectTab'

import CareerStats from './CareerStats'
import ProgressCard from './ProgressCard'
import RecentGames from './RecentGames'
import SeasonStatsGrid from './SeasonStatsGrid'
import SportChips from './SportChips'
import ThisWeekStrip from './ThisWeekStrip'
import SportChipsSkeleton from './skeletons/SportChipsSkeleton'
import ThisWeekStripSkeleton from './skeletons/ThisWeekStripSkeleton'

/** Tall enough that "No Stats Yet" sits mid-page under the tabs, not tucked under them. */
const EMPTY_MIN_HEIGHT = '480px'

interface StatsTabViewProps {
  playerId?: string
}

/** The player Stats tab: sport chips, career stats, this week, progress, recent games, season table. */
const StatsTabView = ({ playerId }: StatsTabViewProps) => {
  'use memo'

  const queryClient = useQueryClient()
  const [isRetryingSports, setIsRetryingSports] = useState(false)
  const {
    selection,
    dispatch,
    sportOptions,
    sportName,
    roles,
    isAllSports,
    showRecentGames,
    statNames,
    sportsState,
    progress,
    thisWeek,
    recent,
    grid,
    careerHighlights,
  } = usePlayerStatsTab({ playerId, timezone: getBrowserTimezone() })

  const roleOptions = roles.map((role) => ({
    value: role,
    label: getRoleTitle(role, sportName),
  }))

  const handleSportChange = (sportKey: string) => {
    dispatch({ type: 'setSport', sportKey })
  }

  const handleRoleChange = (value: string) => {
    const role = roles.find((r) => r === value)
    if (role) {
      dispatch({ type: 'setRole', role })
    }
  }

  const handleRetry = () => {
    if (isRetryingSports) {
      return
    }
    // A refetch with no cached data clears the error immediately, so this
    // flag keeps the centered error up — spinner on the button — until it settles.
    setIsRetryingSports(true)
    queryClient
      .refetchQueries({
        queryKey: queryKeys.playerStats.progressSports(playerId),
      })
      .catch(() => undefined)
      .finally(() => setIsRetryingSports(false))
  }

  // A failed progress load renders nothing here — the Progress card shows the error.
  const renderThisWeek = () => {
    if (thisWeek.isLoading) {
      return <ThisWeekStripSkeleton />
    }
    return thisWeek.data ? <ThisWeekStrip summary={thisWeek.data} /> : null
  }

  if (!sportsState.data && (sportsState.error || isRetryingSports)) {
    return (
      <div css={containerStyles}>
        <LoadErrorState
          title='Could not load stats'
          onRetry={handleRetry}
          loading={isRetryingSports}
          minHeight={EMPTY_MIN_HEIGHT}
        />
      </div>
    )
  }

  // Cold load — also covers the disabled query before `playerId` arrives
  // (React Query v5 reports that as pending, not loading).
  if (!sportsState.data) {
    return (
      <div css={containerStyles}>
        <SportChipsSkeleton />
      </div>
    )
  }

  if (sportsState.data.length === 0) {
    return (
      <div css={containerStyles}>
        <EmptyState
          icon={<LineChartOutlined />}
          title={capitalizeWords('No Stats Yet')}
          subtitle="Stats appear after this player's first game."
          minHeight={EMPTY_MIN_HEIGHT}
        />
      </div>
    )
  }

  return (
    <div css={containerStyles}>
      <div css={chipsRowStyles}>
        <SportChips
          options={sportOptions}
          value={selection.sportKey}
          onChange={handleSportChange}
        />
        {!isAllSports && roles.length > 1 && (
          <SelectTab
            options={roleOptions}
            value={selection.role}
            onChange={handleRoleChange}
          />
        )}
      </div>
      {!isAllSports && (
        <CareerStats highlights={careerHighlights} isLoading={grid.isLoading} />
      )}
      {renderThisWeek()}
      <ProgressCard
        progress={progress}
        selection={selection}
        dispatch={dispatch}
        isAllSports={isAllSports}
        statNames={statNames}
      />
      {!isAllSports && (
        <>
          {showRecentGames && (
            <RecentGames recent={recent} statNames={statNames} />
          )}
          <SeasonStatsGrid
            grid={grid}
            role={selection.role}
            sportName={sportName}
            dispatch={dispatch}
          />
        </>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 24px;
  padding-bottom: 48px;
`

const chipsRowStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

export default StatsTabView
